import { findLast } from 'remeda';

type AdmissionTransaction = {
  admissionTransactionId: string;
  visitorId: string;
  roomSid?: string;
};

const MAX_TRANSACTION_COUNT = 100;
// Keep the newest 20% of transaction history items when max is reached. Keeps
// memory bounded for long-lived provider tabs.
const TRANSACTION_OVERFLOW_RETAIN_COUNT = Math.floor(.2 * MAX_TRANSACTION_COUNT);

/**
 * Singleton holding mutable, non-reactive state used to correlate admission
 * transactions across the admit-flow lifecycle (click → room connect → visitor
 * join).
 */
class AdmissionTrackingHistory {
  private static instance: AdmissionTrackingHistory | null = null;
  private transactions: AdmissionTransaction[] = [];

  private constructor() {}

  static getInstance(): AdmissionTrackingHistory {
    if (!AdmissionTrackingHistory.instance) {
      AdmissionTrackingHistory.instance = new AdmissionTrackingHistory();
    }
    return AdmissionTrackingHistory.instance;
  }

  addTransaction(transaction: AdmissionTransaction): void {
    this.transactions.push(transaction);
    if (this.transactions.length > MAX_TRANSACTION_COUNT) {
      this.transactions = this.transactions.slice(-TRANSACTION_OVERFLOW_RETAIN_COUNT);
    }
  }

  getByTransactionId(admissionTransactionId: string): AdmissionTransaction | undefined {
    return this.transactions.find((transaction) => transaction.admissionTransactionId === admissionTransactionId);
  }

  /**
   * Looks up by visitorId. Returns the most recent match so that a re-admitted
   * visitor's latest admission wins over a stale earlier attempt.
   */
  getByVisitorId(visitorId: string): AdmissionTransaction | undefined {
    return findLast(this.transactions, (transaction) => transaction.visitorId === visitorId);
  }

  /**
   * Looks up by roomSid. Returns the first match so that the
   * room-establishing admission is attributed when multiple admissions share
   * the same room (e.g. a re-admit or multi-visitor session).
   */
  getByRoomSid(roomSid: string): AdmissionTransaction | undefined {
    return this.transactions.find((transaction) => transaction.roomSid === roomSid);
  }

  addRoomSidToTransaction(admissionTransactionId: string, roomSid: string): void {
    const transaction = this.getByTransactionId(admissionTransactionId);
    if (transaction) {
      transaction.roomSid = roomSid;
    }
  }

  getTransactions(): readonly AdmissionTransaction[] {
    return this.transactions;
  }

  reset(): void {
    this.transactions = [];
  }
}

export const admissionTrackingHistory = AdmissionTrackingHistory.getInstance();
